/**
 * The footer's bug report and message form.
 *
 * It lives in one place and only one place: a feedback link on every surface is
 * a feedback link nobody reads. Nothing here asks the visitor to sign in, and
 * the reply address is optional — the report is worth more than the contact
 * detail, and a visitor who just hit a broken page is the person least able to
 * go and make an account first.
 */

import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

import { FEEDBACK_LIMITS, type FeedbackKind } from '@bidtohire/shared';

import { TextAreaField, TextField } from './Fields';
import { ErrorNotice } from './Status';
import { CloseIcon } from './ui';
import { useSubmitFeedback } from '../lib/queries';

const KINDS: readonly { readonly value: FeedbackKind; readonly label: string }[] = [
  { value: 'bug', label: 'Something is broken' },
  { value: 'message', label: 'Something else' },
];

export function FeedbackDialog({ onClose }: { readonly onClose: () => void }): ReactNode {
  const location = useLocation();
  const submit = useSubmitFeedback();

  const [kind, setKind] = useState<FeedbackKind>('bug');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  // The honeypot. A person never sees this field, so anything in it was typed
  // by a script — and the server accepts that submission without acting on it.
  const [website, setWebsite] = useState('');
  const [sent, setSent] = useState(false);

  const firstFieldRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    firstFieldRef.current?.querySelector('textarea')?.focus();
  }, []);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape' && !submit.isPending) onClose();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose, submit.isPending]);

  const trimmed = message.trim();
  const tooShort = trimmed.length < FEEDBACK_LIMITS.messageMinLength;

  function onSubmit(event: FormEvent): void {
    event.preventDefault();
    if (tooShort || submit.isPending) return;

    submit.mutate(
      {
        kind,
        message: trimmed,
        ...(email.trim() === '' ? {} : { email: email.trim() }),
        page: `${location.pathname}${location.search}`,
        ...(website === '' ? {} : { website }),
      },
      { onSuccess: () => setSent(true) },
    );
  }

  return (
    <div
      className="modal-scrim"
      role="dialog"
      aria-modal="true"
      aria-label="Report a bug or send a message"
      onClick={(event) => {
        if (event.target === event.currentTarget && !submit.isPending) onClose();
      }}
    >
      <div className="modal is-narrow">
        <div className="modal-body">
          <div className="row" style={{ alignItems: 'flex-start', flexWrap: 'nowrap', gap: 16 }}>
            <div style={{ flexGrow: 1, minWidth: 0 }}>
              <h2>{sent ? 'Sent' : kind === 'bug' ? 'Report a bug' : 'Send us a message'}</h2>
              <p className="small muted" style={{ marginTop: 6 }}>
                {sent
                  ? 'Thanks — we read everything.'
                  : kind === 'bug'
                    ? 'Tell us what went wrong and we will look at it.'
                    : 'Anything you want us to know — questions, ideas, complaints.'}
              </p>
            </div>
            <button
              type="button"
              className="modal-close"
              aria-label="Close"
              disabled={submit.isPending}
              onClick={onClose}
            >
              <CloseIcon />
            </button>
          </div>

          {sent ? (
            <div className="row" style={{ gap: 10, marginTop: 22 }}>
              <button type="button" className="pill pill-primary" onClick={onClose}>
                Close
              </button>
            </div>
          ) : (
            <form style={{ marginTop: 22 }} onSubmit={onSubmit}>
              <div className="field">
                <span className="fieldlabel">What is this about?</span>
                <div className="row" style={{ gap: 8 }} role="group" aria-label="What is this about?">
                  {KINDS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={kind === option.value ? 'chip is-on' : 'chip'}
                      aria-pressed={kind === option.value}
                      onClick={() => setKind(option.value)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              <div ref={firstFieldRef}>
                <TextAreaField
                  label={kind === 'bug' ? 'What happened?' : 'Your message'}
                  value={message}
                  onChange={setMessage}
                  rows={5}
                  placeholder={
                    kind === 'bug'
                      ? 'What you were doing, and what the page did instead.'
                      : 'Whatever you want to tell us.'
                  }
                  maxLength={FEEDBACK_LIMITS.messageMaxLength}
                  hint={
                    tooShort
                      ? `At least ${FEEDBACK_LIMITS.messageMinLength} characters — ${message.length} so far.`
                      : `${message.length} of ${FEEDBACK_LIMITS.messageMaxLength} characters.`
                  }
                  required
                />
              </div>

              <TextField
                label="Email"
                type="email"
                value={email}
                onChange={setEmail}
                autoComplete="email"
                maxLength={254}
                placeholder="you@company.com"
                hint="Optional. Only used if we need to ask you something."
              />

              <div className="honeypot" aria-hidden="true">
                <label htmlFor="feedback-website">Website</label>
                <input
                  id="feedback-website"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={website}
                  onChange={(event) => setWebsite(event.target.value)}
                />
              </div>

              {submit.isError ? (
                <div style={{ marginTop: 14 }}>
                  <ErrorNotice error={submit.error} />
                </div>
              ) : null}

              <div className="row" style={{ gap: 10, marginTop: 20 }}>
                <button
                  type="submit"
                  className="pill pill-primary"
                  disabled={tooShort || submit.isPending}
                >
                  {submit.isPending ? 'Sending…' : 'Send'}
                </button>
                <button
                  type="button"
                  className="linkbtn"
                  disabled={submit.isPending}
                  onClick={onClose}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
